<template>
  <Dialog
    v-model:visible="dialogVisible"
    :modal="true"
    :draggable="false"
    :closable="true"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    style="width: 70vw"
    @update:visible="updateDialogVisibility"
  >
    <template #header>
      <div>
        <h3>{{ t('storeParameters.services.productRecommendations.modalCategoriesTitle') }}</h3>
      </div>
    </template>
    <div class="container">
      <!-- Search bar -->
      <div class="search-container mb-3">
        <span class="p-input-icon-left w-full">
          <i class="fa-thin fa-magnifying-glass" />
          <InputText
            v-model="category"
            :placeholder="t('storeParameters.services.productRecommendations.searchCategoryPlaceholder')"
            class="w-full"
            @input="handleSearch"
          />
        </span>
      </div>

      <!-- Labels -->
      <div class="flex labels-row mb-2">
        <div class="pane-label available-label">
          <span class="font-semibold">{{ t('storeParameters.services.productRecommendations.availableCategories') }}</span>
          <span class="count-badge">{{ categoriesAvailableList.length }}</span>
        </div>
        <div class="spacer" />
        <div class="pane-label chosen-label">
          <span class="font-semibold">{{ t('storeParameters.services.productRecommendations.selectedCategories') }}</span>
          <span class="count-badge selected">{{ categoriesChosenList.length }}</span>
        </div>
      </div>

      <!-- Main content -->
      <div class="flex pane-container">
        <!-- Available categories -->
        <div class="pane available-pane">
          <div
            v-if="isLoading && categoriesAvailableList.length === 0"
            class="loading-state"
          >
            <i class="fa-solid fa-spinner fa-spin fa-2x" />
            <p>{{ t('storeParameters.services.productRecommendations.loading') }}</p>
          </div>
          <div
            v-else-if="categoriesAvailableList.length === 0 && category.length > 0"
            class="empty-state"
          >
            <i class="fa-thin fa-folder-open fa-3x mb-2" />
            <p>{{ t('storeParameters.services.productRecommendations.noCategoryResults') }}</p>
          </div>
          <div
            v-else-if="categoriesAvailableList.length === 0"
            class="empty-state"
          >
            <i class="fa-thin fa-magnifying-glass fa-3x mb-2" />
            <p>{{ t('storeParameters.services.productRecommendations.searchCategoryHint') }}</p>
          </div>
          <div
            v-else
            ref="availableListRef"
            class="category-list"
            @scroll="handleAvailableScroll"
          >
            <div
              v-for="categoryItem in categoriesAvailableList"
              :key="categoryItem.id_shop_product_category_list"
              class="category-item"
              @click="selectItem(categoryItem)"
            >
              <div class="category-info">
                <span class="category-id">#{{ categoryItem.id_category }}</span>
                <span class="category-name">{{ categoryItem.name }}</span>
              </div>
              <i class="fa-solid fa-plus item-action add" />
            </div>
            <div
              v-if="isLoadingMore"
              class="loading-more"
            >
              <i class="fa-solid fa-spinner fa-spin" />
              <span>{{ t('storeParameters.services.productRecommendations.loadingMore') }}</span>
            </div>
            <div
              v-if="!hasMoreResults && categoriesAvailableList.length > 0"
              class="end-of-list"
            >
              <span>{{ t('storeParameters.services.productRecommendations.endOfList') }}</span>
            </div>
          </div>
        </div>

        <!-- Action hint -->
        <div class="action-buttons">
          <div class="action-hint">
            <i class="fa-solid fa-arrow-right" />
          </div>
        </div>

        <!-- Chosen categories -->
        <div class="pane chosen-pane">
          <div
            v-if="categoriesChosenList.length === 0"
            class="empty-state"
          >
            <i class="fa-thin fa-clipboard-list fa-3x mb-2" />
            <p>{{ t('storeParameters.services.productRecommendations.noCategorySelection') }}</p>
          </div>
          <div
            v-else
            class="category-list"
          >
            <div
              v-for="categoryItem in categoriesChosenList"
              :key="categoryItem.id_shop_product_category_list"
              class="category-item chosen"
              @click="removeItem(categoryItem)"
            >
              <div class="category-info">
                <span class="category-id">#{{ categoryItem.id_category }}</span>
                <span class="category-name">{{ categoryItem.name }}</span>
              </div>
              <i class="fa-solid fa-minus item-action remove" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-content-between align-items-center">
        <span class="selection-summary">
          {{ t('storeParameters.services.productRecommendations.categoriesSelected', { count: categoriesChosenList.length }) }}
        </span>
        <div class="flex gap-2">
          <Button
            class="p-button-secondary"
            @click="handleCancel"
          >
            {{ t('cancel') }}
          </Button>
          <Button
            class="validate-btn"
            @click="handleValidate"
          >
            <i class="fa-solid fa-check mr-2" />
            {{ t('storeParameters.services.productRecommendations.validate') }}
          </Button>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent, PropType, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { ShopsProductsCategoriesList } from '@/types/generated-types/graphql';
import InputText from 'primevue/inputtext';
import ShopsProductsCategoriesListList from '@/composables/shops-products-categories-list/ShopsProductsCategories';

const PAGE_SIZE = 50;

export default defineComponent({
  name: 'CategorySelection',

  components: {
    Dialog,
    Button,
    InputText,
  },

  props: {
    defaultLang: {
      type: String,
      required: true,
    },

    availableCategoriesArr: {
      type: Array as PropType<ShopsProductsCategoriesList[]>,
      required: true,
    },

    chosenCategoriesArr: {
      type: Array as PropType<ShopsProductsCategoriesList[]>,
      required: true,
    },

    categorySearchStr: {
      type: String,
      required: true,
    },
  },

  emits: ['close-action-modal'],

  setup(props, context) {
    const { t } = useI18n();
    const dialogVisible = ref(true);
    const category = ref(props.categorySearchStr);
    const categoriesAvailableList = ref<ShopsProductsCategoriesList[]>([...props.availableCategoriesArr]);
    const categoriesChosenList = ref<ShopsProductsCategoriesList[]>([...props.chosenCategoriesArr]);
    // Stocker les valeurs initiales pour annulation
    const initialChosenList = [...props.chosenCategoriesArr];
    const availableListRef = ref<HTMLElement | null>(null);

    const isLoading = ref(false);
    const isLoadingMore = ref(false);
    const hasMoreResults = ref(true);
    const currentOffset = ref(0);
    let searchTimer: ReturnType<typeof setTimeout> | null = null;
    let searchRequestId = 0;

    const handleCancel = () => {
      dialogVisible.value = false;
      // Retourner les valeurs initiales
      context.emit('close-action-modal', initialChosenList, props.availableCategoriesArr, props.categorySearchStr);
    };

    const handleValidate = () => {
      dialogVisible.value = false;
      context.emit('close-action-modal', categoriesChosenList.value, categoriesAvailableList.value, category.value);
    };

    const updateDialogVisibility = (visible: boolean) => {
      if (!visible) {
        // Fermeture via croix = annulation
        handleCancel();
      }
    };

    const filterOutChosen = (categories: ShopsProductsCategoriesList[]): ShopsProductsCategoriesList[] => categories.filter(
      (c) => !categoriesChosenList.value.some((chosen) => chosen.id_shop_product_category_list === c.id_shop_product_category_list),
    );

    const handleSearch = async () => {
      if (searchTimer) clearTimeout(searchTimer);

      searchRequestId += 1;
      const currentRequestId = searchRequestId;

      const searchTerm = category.value.trim();
      if (!searchTerm) {
        categoriesAvailableList.value = [];
        isLoading.value = false;
        return;
      }

      searchTimer = setTimeout(async () => {
        if (currentRequestId !== searchRequestId) return;

        isLoading.value = true;
        currentOffset.value = 0;
        hasMoreResults.value = true;
        categoriesAvailableList.value = [];

        const result = await ShopsProductsCategoriesListList(category.value, props.defaultLang, PAGE_SIZE, 0);

        if (result === null || currentRequestId !== searchRequestId) return;

        categoriesAvailableList.value = filterOutChosen(result);
        hasMoreResults.value = result.length === PAGE_SIZE;
        currentOffset.value = PAGE_SIZE;
        isLoading.value = false;
      }, 500);
    };

    const loadMore = async () => {
      if (isLoadingMore.value || !hasMoreResults.value) return;

      searchRequestId += 1;
      const currentRequestId = searchRequestId;

      isLoadingMore.value = true;
      const result = await ShopsProductsCategoriesListList(category.value, props.defaultLang, PAGE_SIZE, currentOffset.value);

      if (result === null || currentRequestId !== searchRequestId) {
        isLoadingMore.value = false;
        return;
      }

      if (result.length > 0) {
        const newCategories = filterOutChosen(result);
        categoriesAvailableList.value = [...categoriesAvailableList.value, ...newCategories];
        hasMoreResults.value = result.length === PAGE_SIZE;
        currentOffset.value += PAGE_SIZE;
      } else {
        hasMoreResults.value = false;
      }
      isLoadingMore.value = false;
    };

    const handleAvailableScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = target;

      if (scrollHeight - scrollTop - clientHeight < 100) {
        loadMore();
      }
    };

    const selectItem = (categoryItem: ShopsProductsCategoriesList) => {
      categoriesChosenList.value.unshift(categoryItem);
      categoriesAvailableList.value = categoriesAvailableList.value.filter(
        (c) => c.id_shop_product_category_list !== categoryItem.id_shop_product_category_list,
      );
    };

    const removeItem = (categoryItem: ShopsProductsCategoriesList) => {
      categoriesChosenList.value = categoriesChosenList.value.filter(
        (c) => c.id_shop_product_category_list !== categoryItem.id_shop_product_category_list,
      );
    };

    return {
      t,
      dialogVisible,
      updateDialogVisibility,
      handleCancel,
      handleValidate,
      category,
      handleSearch,
      categoriesAvailableList,
      categoriesChosenList,
      availableListRef,
      selectItem,
      removeItem,
      handleAvailableScroll,
      isLoading,
      isLoadingMore,
      hasMoreResults,
    };
  },
});
</script>

<style scoped lang="scss">
@import '@/assets/scss/utilities/_variables.scss';

.container {
  padding: 1rem;
}

.search-container {
  .p-inputtext {
    padding-left: 2.5rem;
  }
}

.labels-row {
  .pane-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &.available-label {
      width: 45%;
    }

    &.chosen-label {
      width: 45%;
    }
  }

  .spacer {
    width: 10%;
  }

  .count-badge {
    background: $extra-light-grey;
    color: $tuna;
    padding: 0.15rem 0.5rem;
    border-radius: 1rem;
    font-size: 0.8rem;
    font-weight: 600;

    &.selected {
      background: lighten($brand-color-primary, 35%);
      color: darken($brand-color-primary, 20%);
    }
  }
}

.pane-container {
  height: 350px;
  gap: 0;
}

.pane {
  height: 100%;
  border: 1px solid $solitude;
  border-radius: 6px;
  overflow: hidden;
  background: $white;

  &.available-pane {
    width: 45%;
  }

  &.chosen-pane {
    width: 45%;
  }
}

.action-buttons {
  width: 10%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .action-hint {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $extra-light-grey;
    border-radius: 50%;
    color: $mid-grey;
  }
}

.category-list {
  height: 100%;
  overflow-y: auto;
  padding: 0.5rem;
}

.category-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.25rem;

  &:hover {
    background: $extra-light-grey;

    .item-action {
      opacity: 1;
    }
  }

  &.chosen {
    background: lighten($brand-color-primary, 42%);

    &:hover {
      background: lighten($brand-color-primary, 38%);
    }
  }

  .item-action {
    opacity: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    flex-shrink: 0;
    margin-left: auto;
    transition: all 0.2s ease;

    &.add {
      background: $brand-color-primary;
      color: $white;
    }

    &.remove {
      background: #dc3545;
      color: $white;
    }
  }
}

.category-info {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;

  .category-id {
    font-size: 0.75rem;
    color: $mid-grey;
    font-weight: 500;
  }

  .category-name {
    font-size: 0.875rem;
    color: $tuna;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.empty-state,
.loading-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: $mid-grey;
  text-align: center;
  padding: 1rem;

  i {
    color: $mid-grey;
    margin-bottom: 0.5rem;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
  }
}

.loading-more,
.end-of-list {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  color: $mid-grey;
  font-size: 0.85rem;
}

.selection-summary {
  color: $mid-grey;
  font-size: 0.9rem;
}

.validate-btn {
  background: $brand-color-primary;
  border-color: $brand-color-primary;
  color: $white;

  &:hover {
    background: $successButtonHoverBg;
    border-color: $successButtonHoverBg;
  }
}
</style>
